import React from "react";
import { UseFormReturn } from "react-hook-form";
import InputField from "components/controls/InputField";

type Props = {
  name: string;
  label: string;
  form: UseFormReturn<any>;
  required?: boolean;
  disabled?: boolean;
  type?: "text" | "email" | "number" | "password" | "tel";
  min?: number;
  max?: number;
  placeholder?: string;
  contentAfter?: React.ReactNode;
};

const TextInputField = ({
  name,
  label,
  form,
  required = false,
  disabled = false,
  type = "text",
  min,
  max,
  placeholder,
  contentAfter,
}: Props) => {
  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = form;

  const value = watch(name);
  const error = errors[name]?.message as string;

  // Register the field with proper validation for numbers
  React.useEffect(() => {
    if (type === "number") {
      register(name, {
        required: required ? `${label} is required` : false,
        valueAsNumber: false, // Don't use valueAsNumber to handle empty strings
        validate: {
          isNumber: (value) => {
            // Allow null/undefined for optional fields
            if (!required && (value === null || value === undefined || value === "")) {
              return true;
            }
            // For required fields, check if empty
            if (required && (value === null || value === undefined || value === "")) {
              return `${label} is required`;
            }
            // Check if it's a valid number
            const num = Number(value);
            if (isNaN(num)) {
              return `${label} must be a valid number`;
            }
            // Check min/max if provided
            if (min !== undefined && num < min) {
              return `${label} must be at least ${min}`;
            }
            if (max !== undefined && num > max) {
              return `${label} must be at most ${max}`;
            }
            return true;
          },
        },
        setValueAs: (value) => {
          // Handle empty string -> null conversion
          if (value === "" || value === null || value === undefined) {
            return null;
          }
          // Convert to number
          const num = Number(value);
          return isNaN(num) ? null : num;
        },
      });
    } else {
      register(name, {
        required: required ? `${label} is required` : false,
      });
    }
  }, [register, name, required, label, type, min, max]);

  const setFieldValue = (name: string, value: string) => {
    if (type === "number") {
      // For number fields, handle empty string specially
      if (value === "") {
        setValue(name, null, { shouldValidate: true });
      } else {
        const numValue = Number(value);
        setValue(name, isNaN(numValue) ? null : numValue, { shouldValidate: true });
      }
    } else {
      setValue(name, value, { shouldValidate: true });
    }
  };

  // Display value handling for number fields
  const displayValue = type === "number" 
    ? (value === null || value === undefined ? "" : value.toString())
    : (value || "");

  return (
    <InputField
      name={name}
      label={label}
      required={required}
      disabled={disabled}
      value={displayValue}
      setFieldValue={setFieldValue}
      error={error}
      type={type}
      min={min}
      max={max}
      placeholder={placeholder}
      contentAfter={contentAfter}
    />
  );
};

export default TextInputField;
